import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { appointmentService as api, appointmentError, dateKey } from '../../services/appointmentService';
import './appointments.css';

export default function AppointmentBooking() {
  const { providerId } = useParams();
  const [query] = useSearchParams();
  const [calendar, setCalendar] = useState(null);
  const [date, setDate] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(null);
  const request = useRef(0);
  const booking = useRef(false);
  const invalidateRequest = useCallback(() => { request.current++; }, []);
  const refresh = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    try {
      const result = await api.availability(providerId);
      if (current !== request.current) return;
      setCalendar(result);
      setDate(previous => previous || result.slots.find(slot => slot.status === 'available')?.date || result.slots[0]?.date || dateKey(Date.now(), result.timezone));
    } catch (e) { if (current === request.current) setError(appointmentError(e)); }
    finally { if (current === request.current) setLoading(false); }
  }, [providerId]);
  useEffect(() => { setCalendar(null); setDate(''); setSelected(null); setConfirmed(null); refresh(); return invalidateRequest; }, [refresh, invalidateRequest]);
  async function book() {
    if (!selected || booking.current) return;
    booking.current = true; setBusy(true); setError('');
    try {
      const result = await api.book({ providerId: Number(providerId), startAt: selected.startAt,
        ...(query.get('eventId') ? { eventId: Number(query.get('eventId')) } : {}),
        sourceContext: { platform: 'web', source: query.get('source') || 'provider', contextId: query.get('contextId') || null } });
      setConfirmed(result); setSelected(null);
    } catch (e) {
      setError(e?.response?.status === 409 ? 'This slot is no longer available. Availability has been refreshed; choose another time.' : appointmentError(e));
      setSelected(null); await refresh();
    } finally { booking.current = false; setBusy(false); }
  }
  const slots = calendar?.slots.filter(slot => slot.date === date) || [];
  const dates = [...new Set(calendar?.slots.map(slot => slot.date) || [])].sort();
  return <main className="appointments appointment-booking">
    <Link to="/dashboard">Back to dashboard</Link> · <Link to="/dashboard/calendar">My Calendar</Link>
    <h1>Book an Appointment</h1><h2>{query.get('providerName') || 'Provider'}</h2>
    {query.get('eventName')?.trim() && <p>For {query.get('eventName').trim()}</p>}
    <p>Find a time to meet and discuss your plans.</p>
    {error && <p role="alert" className="appointment-error">{error}</p>}
    {confirmed ? <section role="status"><h2>Appointment confirmed</h2><p>{new Date(confirmed.startAt).toLocaleString(undefined, { timeZone: confirmed.timezone })} ({confirmed.timezone})</p><p>Appointment ID: {confirmed.appointmentId || confirmed.id}</p><Link to="/dashboard/calendar">View My Calendar</Link></section> : <>
      {loading ? <p role="status" className="appointment-empty">Finding appointment times…</p> : calendar && !calendar.slots.length ? <section className="appointment-empty" aria-labelledby="appointment-empty-title">
        <span className="appointment-empty-icon" aria-hidden="true">▦</span>
        <h2 id="appointment-empty-title">No times available yet</h2>
        <p>This provider has no appointment times listed for the next 30 days. Please check again later or explore another provider.</p>
        <button onClick={() => { setError(''); refresh(); }}>Refresh availability</button>
        <Link to="/dashboard">Back to providers</Link>
      </section> : calendar ? <>
        <p>Times shown in {calendar.timezone}. {calendar.slotDurationMinutes} minute appointments. Booking is limited to the next 30 days.</p>
        <label>Date <input type="date" value={date} min={dateKey(Date.now(), calendar.timezone)} max={dateKey(calendar.horizonEnd, calendar.timezone)} disabled={busy} onChange={e => { setDate(e.target.value); setSelected(null); }} /></label>
        <div className="appointment-dates" aria-label="Appointment dates">{dates.map(day => <button key={day} type="button" aria-pressed={day === date} disabled={busy} onClick={() => { setDate(day); setSelected(null); }}>{new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</button>)}</div>
        <h3>Choose a time</h3>
        <div className="appointment-slots">{slots.map(slot => <button type="button" key={slot.startAt} disabled={busy || slot.status !== 'available'} aria-pressed={selected?.startAt === slot.startAt} className={`appointment-slot ${slot.status}`} onClick={() => setSelected(slot)}>{slot.localTime} UTC{slot.utcOffset}<br />{slot.status}</button>)}</div>
        {!slots.length && <p>No appointment slots for this date. Try another date.</p>}
        <button disabled={!selected || busy} onClick={book}>{busy ? 'Booking…' : 'Confirm appointment'}</button>
      </> : <section className="appointment-empty"><h2>Unable to load times</h2><button onClick={() => { setError(''); refresh(); }}>Retry availability</button></section>}
    </>}
  </main>;
}
